import React, { ChangeEvent, Fragment, useEffect, useMemo, useRef } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { Button, ButtonText, CapsModal, Empty, H3, LayoutSidebar, SectionHeader, Text, UnallocatedFundsModal } from 'components';
import { ParticipantListItem } from 'components/Calculator/ParticipantListItem';
import { useFeatureFlags } from 'contexts/featureFlags';
import { theme, toRem } from 'design';
import { routeConstants } from 'routes';
import { MPContent } from 'screens/MakePayment/components';
import { selectCurrentEmployee } from 'store/App';
import { selectCalculatorRules } from 'store/CalculatorRules';
import { selectPaymentSetsForSite } from 'store/Collections';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeRole, selectCurrentEmployeeSite, selectCurrentEmployeeType } from 'store/Employers';
import { selectSiteAccountBalance, selectSitePOSIntegration, selectSiteSettings } from 'store/Site';
import { selectCalculatorChangeLog, selectCalculatorPayees, selectCalculatorPaymentSet, selectCalculatorPOSResults, selectCalculatorResults, setCalculatorPayees, setPOSResults, updateCalculatedTipsResults } from 'store/TipCalculator';
import { DisbursementMethod, FeatureFlags, IEmployee, ITipCalculatorPayee, ITipCalculatorResultWarningUnallocatedRemainder, PermissionType, TicketOverrideType, TipCalculatorSafeModeStatus, TipCalculatorWarningType } from 'types';
import { buildTipCalculatorParticipants, buildTipCalculatorWarnings, centsToDollarsDisplay, getRolePolicy, getSharedPOSJobTitles, hasSitePermission, siteUsesHoldbacks, sortAlphaNumerically } from 'utils';
import { roleSiteDisplay } from 'utils/employee';
import EmployeeListItem from '../components/Review/EmployeeListItem/EmployeeListItem';
import Summary from '../components/Review/Summary/Summary';
import { ICalculatorPaymentForm, ISideBarError } from '../types';

interface IReviewProps {
  formik: FormikProps<ICalculatorPaymentForm>;
  isProcessing?: boolean;
  onExpand: () => void;
  pageHeaderContent: React.ReactNode;
  sidebarOpen: boolean;
}

const WarningMessage = styled.div`
  display: flex;
  flex-flow: column;

  button {
    margin-top: ${toRem(12)};
    color: ${theme.color.primary[500]};
    text-align: left;
    text-decoration: none;

    &:hover {
      cursor: pointer;
      color: ${theme.color.primary[600]};
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;

  & > *:not(:last-child) {
    margin-right: ${toRem(8)};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  gap: ${toRem(8)};
`;

const ChangeLogLink = styled(NavLink)`
  font-size: ${toRem(11)};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.primary[600]};
  text-decoration: none;

  &:hover {
    color: ${theme.color.primary[700]};
  }
`;

export const Review: React.FC<IReviewProps> = ({ formik, isProcessing, onExpand, pageHeaderContent, sidebarOpen }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const integration = useSelector(selectSitePOSIntegration());
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentOrg = useSelector(selectCurrentEmployeeOrg);
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);

  const featureFlags = useFeatureFlags([FeatureFlags.TIP_CALCULATOR_DAY_PAID]);
  const dayPaidEnabled = featureFlags[FeatureFlags.TIP_CALCULATOR_DAY_PAID]?.enabled;
  const paymentSetsForCalendarDates = useSelector(selectPaymentSetsForSite());

  const {
    createPayments: createPaymentsEnabled,
    holdback: currentSiteHoldbacks,
    tipCalculatorPercentFee: currentSiteTipCalcPercentFee,
    overdraftLimit = 0,
    perTipMax = 0,
    paymentCategories,
    rolePolicy,
    tipCalculatorSafeMode,
  } = useSelector(selectSiteSettings()) || {};
  const currentEmployeePolicy = getRolePolicy(rolePolicy, currentEmployeeType, currentEmployeeRole);

  const siteBalance = useSelector(selectSiteAccountBalance());
  const payees = useSelector(selectCalculatorPayees());
  const dataCyBase = useRef('review-list').current;
  const tipCalcResults = useSelector(selectCalculatorResults());
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const participants = buildTipCalculatorParticipants(tipCalcResults);

  // POS Data
  const posResults = useSelector(selectCalculatorPOSResults());
  const hasSharedPOS = posResults.data.some((data) => !data.employee && getSharedPOSJobTitles(currentCalculatorRules).includes(data.provider.schedules[0]?.jobTitle.toLowerCase()));
  const paymentSet = useSelector(selectCalculatorPaymentSet());

  // Change log entries
  const changeLogEntries = useSelector(selectCalculatorChangeLog());

  useEffect(() => {
    if (!paymentSet) history.push(routeConstants.private.makePayment);
  }, [paymentSet, history]);

  const onAgreementChange = async (e: ChangeEvent<HTMLInputElement>) => {
    await formik?.setFieldValue('authorizedAgreement', e.target.checked);
    formik?.setFieldTouched('authorizedAgreement');
  };

  const onCancel = () => {
    history.push(routeConstants.private.makePayment);
  };

  const onDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik?.setFieldValue('description', e.target.value);
  };

  const onPaymentCategoryChange = (value: string) => {
    formik?.setFieldValue('paymentCategory', value);
  };

  const onSubmit = () => {
    /**
     * submission should not be able to occur if there is an invalid
     * payee found, but just in case, adding an additional check here.
     */
    if (payees.find((payee) => !payee.employee)) return;

    formik.setFieldValue('payees', payees);
    formik.handleSubmit();
  };

  const participantList = useMemo(
    () => participants?.sort(sortAlphaNumerically('name')).map((participant) => <ParticipantListItem key={`participant_${participant.name}`} participant={participant} calculatorRules={currentCalculatorRules} />),
    [participants, currentCalculatorRules]
  );

  const payeesList = useMemo(() => {
    if (!payees.length) return <Empty container containerHeight={250} icon="People" message="No Payees" />;

    // Update the holdback amount for a payee
    const onHoldbackChange = (tipCalcPayee: ITipCalculatorPayee) => (holdback: number) => {
      const updateTipCalcPayees = payees.map((payee) => (payee.id === tipCalcPayee.id ? { ...payee, holdbackAmount: holdback, net: payee.gross - holdback } : payee));
      dispatch(setCalculatorPayees(updateTipCalcPayees));
    };

    // Update the note for a payee
    const onNoteChange = (tipCalcPayee: ITipCalculatorPayee) => (note: string) => {
      const updateTipCalcPayees = payees.map((payee) => (payee.id === tipCalcPayee.id ? { ...payee, note } : payee));
      dispatch(setCalculatorPayees(updateTipCalcPayees));
    };

    // Update the payees with the invited employee
    const onInviteSuccess = (employee: IEmployee) => {
      const site = employee.sites.find((s) => s.id === currentSite?.id);
      if (!site) return;

      const updated = payees.map((payee) => {
        // use the employee integrations if they exist because if the employee
        // id was changed in the invite flow, this is the only place we have a
        // reference to the original provider id in order to match the new
        // employee to the existing payee.
        const employeeIntegration = (employee.integrations || []).find((i) => i.id === payee.provider?.employeeId);
        if (payee.provider.employeeId !== site.employeeId && !employeeIntegration) return payee;

        // update the employee id on the calculated results
        tipCalcResults.forEach((result) => {
          const resultEmployees = result.employees.filter((employee) => employee.id === payee.provider?.employeeId);

          // update the employee id for all results
          resultEmployees.forEach((item) => {
            // eslint-disable-next-line no-param-reassign
            item.id = employee.id;
          });
        });

        // build the updated employee
        const updatedEmployee = {
          id: employee.id,
          name: employee.name,
          firstName: employee.firstName,
          lastName: employee.lastName,
          employeeId: site.employeeId,
          job: { title: site.job.title },
          role: site.role,
          type: employee.type,
          disbursementMethod: Object.values(DisbursementMethod).find((method) => method === site.disbursementMethod),
        };

        // update labor & ticket data
        const laborTicketDataEmployee = posResults.data.find((item) => !item.employee && item.provider.employeeId === payee.provider?.employeeId);
        if (laborTicketDataEmployee) laborTicketDataEmployee.employee = updatedEmployee;

        // update split tickets with new employee data
        posResults.data.forEach((data) => {
          data.segments.forEach((segment) => {
            segment.tickets.forEach((ticket) => {
              if (ticket.override?.type === TicketOverrideType.SPLIT) {
                const share = ticket.override.shares.find((share) => share.participant.id === payee.provider?.employeeId);
                if (share) {
                  share.participant.id = updatedEmployee.id;
                  share.participant.name = updatedEmployee.name;
                }
              }
            });
          });
        });

        return {
          ...payee,
          name: updatedEmployee.name,
          employee: updatedEmployee,
          ...(site.disbursementMethod === DisbursementMethod.PAYROLL && { holdbackAmount: 0, net: payee.gross }),
        };
      });

      dispatch(setCalculatorPayees(updated)); // update payees
      dispatch(setPOSResults(posResults)); // update labor data
      dispatch(updateCalculatedTipsResults(tipCalcResults)); // update calculator results
    };

    return payees
      .sort(sortAlphaNumerically('name'))
      .map((payee) => (
        <EmployeeListItem
          segments={payee.segments}
          currentCalculatorRules={currentCalculatorRules}
          currentEmployee={currentEmployee}
          currentEmployeePolicy={currentEmployeePolicy}
          currentOrg={currentOrg}
          rolePolicy={rolePolicy}
          dataCy={`${dataCyBase}-item`}
          holdback={siteUsesHoldbacks(currentSiteHoldbacks) ? payee.holdbackAmount : undefined}
          integration={integration}
          isLoading={false}
          key={payee.id}
          maxTipAmount={perTipMax}
          note={payee.note}
          onHoldbackChange={onHoldbackChange(payee)}
          onInvited={onInviteSuccess}
          onNoteChange={onNoteChange(payee)}
          payee={payee}
          creditCardFee={currentSiteTipCalcPercentFee}
        />
      ));
  }, [payees, currentCalculatorRules, posResults, tipCalcResults]);

  const isOverdraft = useMemo((): ISideBarError => {
    const totalInstantTips = Math.round((payees || []).filter((p) => p.employee?.disbursementMethod === DisbursementMethod.INSTANT).reduce((acc: number, payee: ITipCalculatorPayee) => (payee.gross || 0) + acc, 0));
    const totalHoldbacks = Math.round((payees || []).reduce((acc, payee: ITipCalculatorPayee) => (payee.holdbackAmount || 0) + acc, 0));
    const netInstant = totalInstantTips - totalHoldbacks;
    const totalBalancePlusOverdraft = (siteBalance || 0) + overdraftLimit;
    const balance = isNil(siteBalance) ? 0 : (siteBalance as number);
    const newBalance = netInstant - balance;

    // do not show if net payout is equal or less than 0
    if (netInstant <= 0) return undefined;

    if (netInstant > 0 && netInstant > balance && netInstant <= totalBalancePlusOverdraft) {
      return {
        preventSubmit: false,
        message: `Your new balance will be ${newBalance <= 0 ? `${centsToDollarsDisplay(newBalance)}` : `-${centsToDollarsDisplay(newBalance)}`}. Your limit is -${centsToDollarsDisplay(overdraftLimit)}.`,
      };
    }

    if (netInstant > totalBalancePlusOverdraft) {
      return {
        preventSubmit: true,
        message: `Your new balance is below the limit of ${overdraftLimit > 0 ? `-${centsToDollarsDisplay(overdraftLimit)}` : centsToDollarsDisplay(overdraftLimit)}. Payment cannot be accepted.`,
      };
    }

    return undefined;
  }, [payees]);

  const isNegativePayment = useMemo((): ISideBarError => {
    if ((payees || []).filter((p) => Math.floor(p.gross || 0) - Math.round(p.holdbackAmount || 0) < 0).length > 0) {
      return {
        preventSubmit: true,
        title: 'Negative Tip Amount',
        message: 'Please adjust holdback or update affected employees to resolve.',
      };
    }

    return undefined;
  }, [payees]);

  // Get Tip Calculator Warnings
  const tipCalculatorWarnings = useMemo(() => buildTipCalculatorWarnings(tipCalcResults), [tipCalcResults]);

  // Filter for cap warnings
  const cappedWarnings = useMemo(() => tipCalculatorWarnings.filter((warning) => [TipCalculatorWarningType.CAPPED_REMAINDER, TipCalculatorWarningType.CAPPED_SHARE].includes(warning.type)), [tipCalculatorWarnings]);

  // Filter for unallocated funds warnings
  const unallocatedFundsWarnings = useMemo(
    () => tipCalculatorWarnings.filter((warning) => [TipCalculatorWarningType.UNALLOCATED_REMAINDER].includes(warning.type)) as ITipCalculatorResultWarningUnallocatedRemainder[],
    [tipCalculatorWarnings]
  );

  const [showCapWarningModal, hideCapWarningdModal] = useModal(() => <CapsModal warnings={cappedWarnings} hideModal={hideCapWarningdModal} />, [cappedWarnings]);

  const isCappedWarning = useMemo((): ISideBarError => {
    if (cappedWarnings.length > 0) {
      const messageBody = (
        <WarningMessage>
          <Text size="small">Some tip share amounts have been capped because there weren't enough tips or autograts collected to fully fund the sharing rules.</Text>
          <ButtonText label="View Details" onClick={showCapWarningModal} />
        </WarningMessage>
      );

      return {
        title: 'Tip Share Amounts Capped',
        message: messageBody,
        preventSubmit: false,
      };
    }

    return undefined;
  }, [cappedWarnings]);

  const [showUnallocatedFundsWarningModal, hideUnallocatedFundsWarningdModal] = useModal(
    () => <UnallocatedFundsModal warnings={unallocatedFundsWarnings} tipCalcResults={tipCalcResults} hideModal={hideUnallocatedFundsWarningdModal} />,
    [unallocatedFundsWarnings, tipCalcResults]
  );

  const isUnallocatedFundsWarning = useMemo((): ISideBarError => {
    if (unallocatedFundsWarnings.length > 0) {
      const totalUnallocated = unallocatedFundsWarnings.reduce((sum, warning) => sum + (warning.remainder?.unallocated?.total ?? 0), 0);
      const messageBody = (
        <WarningMessage>
          <Text size="small">{centsToDollarsDisplay(totalUnallocated)} of tips collected cannot be disbursed. If you proceed, these tips will not be paid by Kickfin.</Text>
          <ButtonText label="View Details" onClick={showUnallocatedFundsWarningModal} />
        </WarningMessage>
      );

      return {
        title: 'Unallocated Tips',
        message: messageBody,
        preventSubmit: false,
      };
    }

    return undefined;
  }, [unallocatedFundsWarnings]);

  const matchNotFound = useMemo((): ISideBarError => {
    if (payees?.some((payee) => !payee.employee)) {
      return {
        preventSubmit: true,
        title: 'User setup not complete',
        message: 'Please review and complete setup for all users.',
      };
    }

    return undefined;
  }, [payees]);

  const ineligiblePayees = useMemo((): ISideBarError => {
    const self = currentEmployee?.id && payees?.find((payee) => payee.employee?.id === currentEmployee.id);
    if (self) {
      const currentPolicy = getRolePolicy(rolePolicy, self.employee.type, self.employee.role);
      if (currentPolicy && !currentPolicy.paySelf) {
        return {
          preventSubmit: true,
          title: 'Self Payment',
          message: 'The site role policies restrict payment to yourself.',
        };
      }
    }

    const ineligiblePayees = payees?.filter((payee) => {
      const currentPolicy = payee.employee && getRolePolicy(rolePolicy, payee.employee.type, payee.employee.role);
      return currentPolicy && !currentPolicy.payable;
    });

    if (ineligiblePayees?.length) {
      return {
        preventSubmit: true,
        title: 'Ineligible Payees',
        message: (
          <>
            The following users are ineligible to receive tips due to their selected role:
            <br />
            {ineligiblePayees.map((payee) => (
              <Fragment key={payee.id}>
                {payee.employee.name} - {roleSiteDisplay(payee.employee.role)}
                <br />
              </Fragment>
            ))}
          </>
        ),
      };
    }
    return undefined;
  }, [payees, currentEmployee, rolePolicy]);

  const summary = useMemo(() => {
    const overdraftError = isOverdraft;
    const negativePayoutError = isNegativePayment;
    const cappedWarning = isCappedWarning;
    const unallocatedFundsWarning = isUnallocatedFundsWarning;

    let altError: ISideBarError;

    let preventSubmit = payees.length === 0;
    let totalHoldbacks = 0;
    let totalInstant = 0;
    let totalPayroll = 0;
    let total = 0;
    let instantDisbursementFound = false;
    let individualPayeeNetNotPos = false;
    let individualPayeeNetTooLarge = false;

    // prevent submit when there are no payees
    if (payees?.length === 0) {
      altError = {
        title: 'No Payees',
        message: 'There are no eligible payees for this date.',
        preventSubmit: true,
      };
    }

    // prevent submit when the day has already been paid
    if (dayPaidEnabled && paymentSetsForCalendarDates?.find((item) => item.paymentDate === paymentSet?.date)) {
      altError = {
        title: 'Already Paid',
        message: 'You have already issued tips for this day.',
        preventSubmit: true,
      };
    }

    payees?.forEach((payee) => {
      const disbursementMethod = payee.employee ? payee.employee.disbursementMethod : undefined;
      if (disbursementMethod === DisbursementMethod.INSTANT && !instantDisbursementFound) instantDisbursementFound = true;

      /**
       * if a payee does not have an employee object associated with it
       * it means we received their data from the POS, but there
       * was no match found in the Kickfin system. in this event, this
       * payee must first be invited to Kickfin before we can proceed.
       */
      if (!payee.employee || overdraftError?.preventSubmit === true || negativePayoutError?.preventSubmit === true) {
        preventSubmit = true;
      }

      totalHoldbacks += payee?.employee?.disbursementMethod === DisbursementMethod.INSTANT ? payee.holdbackAmount : 0;
      totalInstant += payee?.employee?.disbursementMethod === DisbursementMethod.INSTANT ? payee.gross - payee.holdbackAmount : 0;
      totalPayroll += payee?.employee?.disbursementMethod === DisbursementMethod.PAYROLL ? payee.gross : 0;
      total += payee.gross;

      const net = payee.gross - payee.holdbackAmount;

      if (payee?.employee?.disbursementMethod === DisbursementMethod.INSTANT && net < 1) individualPayeeNetNotPos = true;
      if (net > perTipMax) individualPayeeNetTooLarge = true;
    });

    if (instantDisbursementFound && totalInstant < 1) {
      altError = {
        title: 'Invalid Pay Amount',
        message: `The amount to pay must be greater than $0.00. To resolve this, please adjust the tip ${currentSiteHoldbacks && instantDisbursementFound ? 'or holdback ' : ''}amounts for the employees being paid.`,
        preventSubmit: true,
      };
    }

    if (!altError && individualPayeeNetNotPos) {
      altError = {
        title: 'Invalid Pay Amount',
        // eslint-disable-next-line max-len
        message: `One or more of your employees has an invalid tip amount. To resolve this, please adjust the tip ${currentSiteHoldbacks && instantDisbursementFound ? ' or holdback ' : ''}amounts for the employees being paid so each employee has a tip amount greater than $0.00.`,
        preventSubmit: true,
      };
    }

    if (!altError && individualPayeeNetTooLarge) {
      altError = {
        title: 'Invalid Pay Amount',
        // eslint-disable-next-line max-len
        message: `One or more of your employees has an invalid tip amount. To resolve this, please adjust the tip amounts for the employees being paid so each employee has a tip amount of ${centsToDollarsDisplay(perTipMax)} or less.`,
        preventSubmit: true,
      };
    }

    // Validate the user has valid permissions to submit the payment.
    if (!altError && !hasSitePermission(currentSite?.permissions, PermissionType.PAYMENT_SUBMIT)) {
      altError = {
        title: 'Permission Denied',
        message: 'You do not have permissions to submit payments. Please contact Kickfin support.',
        preventSubmit: true,
      };
    }

    //  Validate payments are enabled
    if (!altError && !createPaymentsEnabled) {
      altError = {
        title: 'Payments Disabled',
        message: 'Payments are disabled for this site. You can not submit payments at this time.',
        preventSubmit: true,
      };
    }

    //  Validate tip calculator is not in safe mode
    if (!altError && tipCalculatorSafeMode === TipCalculatorSafeModeStatus.ENABLED) {
      altError = {
        title: 'Safe Mode',
        message: 'Payments cannot be submitted while the tip calculator is in Safe Mode.',
        preventSubmit: true,
      };
    }

    return (
      <Summary
        authorizedAgreement={formik.values.authorizedAgreement}
        description={formik.values.description}
        paymentCategory={formik.values.paymentCategory}
        paymentCategories={paymentCategories}
        isProcessing={isProcessing}
        onAgreementChange={onAgreementChange}
        onDescriptionChange={onDescriptionChange}
        onPaymentCategoryChange={onPaymentCategoryChange}
        onExpand={onExpand}
        onPrimaryAction={onSubmit}
        onSecondaryAction={onCancel}
        paymentDate={dayjs(paymentSet?.date)}
        preventSubmit={preventSubmit}
        totalHoldbacks={totalHoldbacks}
        sidebarOpen={sidebarOpen}
        totalInstantTips={totalInstant}
        totalPayrollTips={totalPayroll}
        totalTips={total}
        participants={participants}
        cappedWarning={cappedWarning}
        unallocatedFundsWarning={unallocatedFundsWarning}
        error={negativePayoutError || altError || matchNotFound || ineligiblePayees || overdraftError}
      />
    );
  }, [formik, payees, currentSite, paymentSet]);

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}

          <MPContent data-cy={dataCyBase}>
            <SectionHeader
              data-cy={`${dataCyBase}-header`}
              title={
                <TitleWrapper>
                  <H3>Payees ({(payees?.length || 0) + (participants?.length || 0)})</H3>
                  {changeLogEntries.length > 0 && (
                    <ChangeLogLink to={routeConstants.private.makePaymentCalculatorChangeLog} data-cy="view-change-log-button">
                      View Changes
                    </ChangeLogLink>
                  )}
                </TitleWrapper>
              }
            >
              <ButtonsContainer>
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-pools-button-link`} label="Pools" to={routeConstants.private.makePaymentCalculatorPools} />
                {hasSharedPOS && <Button appearance="white" size="small" data-cy={`${dataCyBase}-shared-pos-button-link`} label="Shared POS" to={routeConstants.private.makePaymentCalculatorSharedPOS} />}
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-checks-button-link`} label="Checks" to={routeConstants.private.makePaymentCalculatorChecks} />
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-labor-data-button-link`} label="Labor Data" to={routeConstants.private.makePaymentCalculatorLaborData} />
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-tip-data-button-link`} label="Tip Data" to={routeConstants.private.makePaymentCalculatorTipAmounts} />
              </ButtonsContainer>
            </SectionHeader>
            {participantList}
            {payeesList}
          </MPContent>
        </>
      }
      sidebar={summary}
      sidebarOpen={sidebarOpen}
    />
  );
};
